<script setup>
import { useApi } from '@/utils/api'
import { useApiRequest } from '@/composables/useApiRequest'

// Addendum 09, Modul AI — bikin campaign: pilih template + kriteria
// segmentasi (Modul AG), preview jumlah/daftar customer yang match SEBELUM
// benar-benar bikin campaign (§2.1 addendum: "owner perlu lihat dulu 'kena
// berapa orang' sebelum kirim"), baru submit -> generate broadcast_recipients.

definePage({
  meta: {
    layout: 'default',
    action: 'create',
    subject: 'Broadcast',
  },
})

const router = useRouter()
const { loading, errors, request } = useApiRequest()

// ─── Template ────────────────────────────────────────────────────────────
const templates = ref([])
const templateId = ref(null)

onMounted(async () => {
  try {
    const response = await useApi('/api/v1/broadcast-templates', {
      query: { channel: 'whatsapp', is_active: 1, per_page: 100 },
    }).get()

    const body = response?.data ?? response

    templates.value = body?.data ?? []
  } catch (err) {
    console.error('[BroadcastCampaignCreate] gagal memuat template', err)
  }
})

const templateOptions = computed(() =>
  templates.value.map(t => ({ title: t.name, value: t.id })),
)

const selectedTemplate = computed(() => templates.value.find(t => t.id === templateId.value) ?? null)

// ─── Kriteria segmentasi (sama field dengan Modul AG) ───────────────────────
const inactiveDays = ref(null)
const minTotalSpent = ref(null)
const birthdayThisMonth = ref(false)

const criteria = computed(() => {
  const c = {}
  if (inactiveDays.value) c.inactive_days = Number(inactiveDays.value)
  if (minTotalSpent.value !== null && minTotalSpent.value !== '') c.min_total_spent = Number(minTotalSpent.value)
  if (birthdayThisMonth.value) c.birthday_this_month = true
  
  return c
})

const hasCriteria = computed(() => Object.keys(criteria.value).length > 0)

// ─── Preview audiens ────────────────────────────────────────────────────────
const previewLoading = ref(false)
const previewError = ref('')
const previewResult = ref(null) // { count, customers }

const runPreview = async () => {
  if (!hasCriteria.value) {
    previewResult.value = null
    
    return
  }

  previewLoading.value = true
  previewError.value = ''
  try {
    const response = await useApi('/api/v1/crm/customers/segment', { query: criteria.value }).get()
    const body = response?.data ?? response

    previewResult.value = body?.data ?? null
  } catch (err) {
    previewError.value = err?.data?.message ?? 'Gagal memuat preview audiens.'
    previewResult.value = null
  } finally {
    previewLoading.value = false
  }
}

// ─── Submit ───────────────────────────────────────────────────────────────
const submit = async () => {
  const result = await request(() =>
    useApi('/api/v1/broadcast-campaigns').post({
      body: { template_id: templateId.value, criteria: criteria.value },
    }),
  )

  if (result !== null) {
    const body = result?.data ?? result
    const campaign = body?.data ?? body

    router.push({ name: 'crm-broadcast-campaigns-id', params: { id: campaign.id } })
  }
}
</script>

<template>
  <VRow>
    <VCol
      cols="12"
      md="7"
    >
      <VCard>
        <VCardTitle class="pt-5 px-5">
          Buat Campaign Broadcast WhatsApp
        </VCardTitle>
        <VCardText class="text-body-2 text-medium-emphasis px-5 pt-0">
          Sistem akan membuat antrian pesan per customer yang cocok kriteria di bawah. Mengirim tetap
          klik manual satu-satu di halaman antrian setelah campaign dibuat — bukan kirim otomatis.
        </VCardText>

        <VDivider />

        <VCardText class="pa-5">
          <VAlert
            v-if="errors._general"
            type="error"
            variant="tonal"
            class="mb-4"
          >
            {{ errors._general[0] }}
          </VAlert>

          <VSelect
            v-model="templateId"
            :items="templateOptions"
            label="Template Pesan *"
            :error-messages="errors.template_id"
            class="mb-4"
          />

          <VAlert
            v-if="selectedTemplate"
            type="info"
            variant="tonal"
            density="compact"
            class="mb-4"
          >
            <div class="text-caption text-medium-emphasis mb-1">
              Isi pesan template:
            </div>
            <div style="white-space: pre-wrap">
              {{ selectedTemplate.body_template }}
            </div>
          </VAlert>

          <div class="text-subtitle-2 mb-2">
            Kriteria Audiens (minimal pilih 1)
          </div>

          <VRow>
            <VCol
              cols="12"
              md="6"
            >
              <VTextField
                v-model="inactiveDays"
                type="number"
                min="1"
                label="Tidak belanja X hari terakhir"
                suffix="hari"
                hint="Termasuk customer yang belum pernah belanja sama sekali"
                persistent-hint
              />
            </VCol>
            <VCol
              cols="12"
              md="6"
            >
              <VTextField
                v-model="minTotalSpent"
                type="number"
                min="0"
                label="Total belanja minimal (VIP)"
                prefix="Rp"
              />
            </VCol>
            <VCol cols="12">
              <VCheckbox
                v-model="birthdayThisMonth"
                label="Ulang tahun bulan ini"
                hide-details
              />
            </VCol>
          </VRow>

          <div
            v-if="errors.criteria"
            class="text-error text-caption mt-1"
          >
            {{ Array.isArray(errors.criteria) ? errors.criteria[0] : errors.criteria }}
          </div>

          <VBtn
            class="mt-4"
            variant="tonal"
            :loading="previewLoading"
            :disabled="!hasCriteria"
            @click="runPreview"
          >
            Preview Audiens
          </VBtn>
        </VCardText>

        <VDivider />

        <VCardActions class="pa-5">
          <VSpacer />
          <VBtn
            variant="tonal"
            :to="{ name: 'crm-broadcast-campaigns' }"
          >
            Batal
          </VBtn>
          <VBtn
            color="primary"
            :loading="loading"
            :disabled="!templateId || !hasCriteria"
            @click="submit"
          >
            Buat Campaign
          </VBtn>
        </VCardActions>
      </VCard>
    </VCol>

    <VCol
      cols="12"
      md="5"
    >
      <VCard>
        <VCardTitle class="text-body-1">
          Preview Audiens
        </VCardTitle>
        <VDivider />
        <VCardText>
          <VAlert
            v-if="previewError"
            type="error"
            variant="tonal"
            density="compact"
          >
            {{ previewError }}
          </VAlert>
          <template v-else-if="previewResult">
            <div class="text-h6 mb-2">
              {{ previewResult.count }} customer cocok kriteria
            </div>
            <VList
              density="compact"
              class="mb-0"
            >
              <VListItem
                v-for="c in previewResult.customers"
                :key="c.id"
                :title="c.name"
                :subtitle="c.phone"
              />
            </VList>
            <div
              v-if="previewResult.count === 0"
              class="text-caption text-medium-emphasis"
            >
              Tidak ada customer yang cocok — campaign tidak bisa dibuat sampai ada yang cocok.
            </div>
          </template>
          <div
            v-else
            class="text-caption text-medium-emphasis"
          >
            Klik "Preview Audiens" untuk lihat siapa yang akan masuk campaign ini sebelum dibuat.
          </div>
        </VCardText>
      </VCard>
    </VCol>
  </VRow>
</template>
